// src/components/JarvislyModule/PageHeader/GoBackButton.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useEffect } from 'react';
import { Button, Tooltip } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import translateX from 'utils/translateX';
import { useModuleStore } from 'stores/module';
import { useModuleContext } from 'components/core/ModuleProvider';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
export const GoBackButton = ({
  actions = [],
  actionContext,
  label,
  tooltip = translateX('back_to_home'),
  icon,
  type,
  disabled,
  onClick,
  show, // override: if provided as true, bypasses the settings.actions check
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const setPageBackStore = useZustand('module', 'setPageBack');
  const storedCurrentPage = useZustand('module', 'currentPage');
  const setLoadingPageStore = useZustand('boot', 'setLoadingPage');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const fallbackNavigate = useNavigate();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = actionContext?.navigate || fallbackNavigate;
  const computedDisabled = disabled;

  // Nova estrutura: header.buttons.goBack controla visibilidade
  // const showGoBackButton = storedCurrentPage?.header?.buttons?.goBack === true;
  // show prop overrides the settings.actions check (used for direct placements in TitleAndButtons)
  const showGoBackButton =
    show === true || storedCurrentPage?.settings?.actions?.includes('return');

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClick = () => {
    // Hide affixed toolbar immediately before navigating (prevents visual glitch)
    actionContext?.setIsNavigatingAway?.(true);
    actionContext?.setIsAffixed?.(false);

    // Show loading immediately
    setLoadingPageStore({ visible: true });

    // Double requestAnimationFrame ensures browser paints loading before navigation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPageBackStore({ navigate });
      });
    });
  };

  // Prevent blur from stealing focus before click registers
  const handleMouseDown = e => {
    e.preventDefault();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const buttonIcon = icon || <ArrowLeftOutlined />;
  const buttonType = type;
  const title = translateX(tooltip || label || 'return');

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!showGoBackButton) return null;

  return (
    <Tooltip title={title} placement="top">
      <Button
        type={buttonType}
        style={computedDisabled ? { backgroundColor: '#ffffff' } : undefined}
        disabled={computedDisabled}
        icon={buttonIcon}
        onClick={onClick || handleClick}
        onMouseDown={handleMouseDown}
      >
        {isLabelProvided(label) ? translateX(label) : translateX('go_back')}
      </Button>
    </Tooltip>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default GoBackButton;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function isLabelProvided(label) {
  return !!label && typeof label === 'string';
}
